import {IScene} from "../../peregrine/processor";
import {DeepPartial} from "ts-essentials";
import {ISimpleCommand} from "../../app/core";

export interface IGAR {
  success: boolean
  message?: string
  ids?: string[]
  info?: any
}

export interface IAttachment {
  title: string
  thumbnail: string
  file: string
}

export interface IProjectAccess {
  username: string
  userAvatar: string
  isLiked: boolean
  isBookmarked: boolean
  isCollab: boolean
}

export interface IProjectSummary {
  id: number
  thumbnail: string
  thumbnailWidth: number
  thumbnailHeight: number
  thumbnailZoom: number
  isDirectory: boolean
}

export interface IProjectInfo {
  manuDays?: number
  productionCost?: number
  weight?: number
  materialName?: string
  minQuantity?: number
  special?: string
  vertexCount?: number
  faceCount?: number
  fileSize?: number
  renderVertexCount?: number
  renderFaceCount?: number
  renderedObjCount?: number
  noBulkOrder?: boolean
  maxBulkDiscount?: number
  minBulkOrder?: number
}

export interface IProcessorModelPreview {
  boundingBoxSize: [number, number, number]
}

export interface IProcessor {
  id: string
  name: string
  projectId: number
  calcIds: string[]
  modelPreview: IProcessorModelPreview
}

export interface IProjectFetchConfig {
  mode: string
  rootId: number
  sortBy: string
  needle: string
  username: string
  count: number
  dontReplace: boolean
}

export enum RenderSessionState {
  Running,
  Finished
}

export interface IRenderSession {
  id: number
  thumbnail: string
  state: number
  isLive: boolean
  spp: number
  convergence: number
  frameNo: number
  config: {
    width: number,
    height: number
  }
  createdDate: Date
}

export interface IProject {
  id: number
  title: string
  username: string
  userAvatar: string
  screenshotIds: number[]
  createdDate: Date
  lastModifiedDate: Date
  sharedSlug?: string
  sharingPassword: string
  snapshot: any
  preview: string
  thumbnail: string
  thumbnailZoom: number
  thumbnailWidth: number
  thumbnailHeight: number
  allowForking: boolean
  allowDownload: boolean
  privateChatSessionId: number
  publicChatSessionId: number
  bugChatSessionId: number
  otherChatSessionIds: number[]
  description: string
  fullDescription: string
  isPublic: boolean
  isGlobal: boolean
  isLiveStream: boolean
  attachments: IAttachment[]
  projectAccesses: IProjectAccess[]
  innerProjects: IProjectSummary[]
  renderSessions: IRenderSession[]
  totalViews: number
  bookmarkCount: number
  info: IProjectInfo
  isDirectory: boolean
  directoryId: number
  scn: IScene
}

export type IPostProject =
  Partial<IProject>
  & { scene?: any, digitalMaterials?: any, diff?: boolean, shuffle?: boolean };

export interface IChatSession {
  id: number
  title: string
  description: string
  image: string
  createdDate: Date
  typingUsernames: string[]
}

export interface IScreenshot {
  id: number
  projectId: number
  thumbnail: string
  title: string
}

export interface IChannelRoute {
  id: number
  projectId: number
  username: string
  userAvatar: string
  showCursor?: boolean
  syncCamera?: boolean
}

export interface IProjectDynamic {
  channelRoutes: { [id: number]: { [id: number]: IChannelRoute } }
  serverCommands: ISimpleCommand[]
  serverCursor: number
  routeStartCursor: number
  commands: ISimpleCommand[]
  cursor: number
}

export interface IChatAccess {
  username: string
  isRead: boolean
  actionText: string
}

export interface IChat {
  id: number
  createdDate: Date
  chatSessionId: number
  chatSessionTitle: string
  projectId: number
  replyTo: number
  content: string
  username: string
  userAvatar: string
  chatAccesses: IChatAccess[]
  attachments: IAttachment[]
}

export interface INotification {
  id: number
  username: string
  projectId: number
  message: string
  isRead: boolean
  createdDate: Date
}

export interface IChatBotMessage {
  id: number
  message: string,
  isInput: boolean,
  createdDate: number
}

export interface ILibraryTopCategory {
  id: number
  title: string
  categoryIds: number[]
}

export interface ILibraryCategory {
  id: number
  title: string
  topCategoryId: number
  itemIds: string[]
}

export enum LibraryItemTypes {
  DigitalMaterial = 'digital material',
  Environment = 'environment',
  Light = 'light',
  Processor = 'processor'
}

export interface ILibraryItem {
  id: string
  title: string
  entityType: string
  entityId: string
  image: string
  categoryId: number
  projectId: number
  username: string
  hashtags: string[]
}

export interface IPBRTexture {
  url: string
  wrapS?: string
  wrapT?: string
  repeat?: [number, number]
  offset?: [number, number]
}

export interface IPBRDigitalMaterial {
  color: string
  metalness: number
  roughness: number
  clearcoat: number
  clearcoatRoughness: number
  sheen: string
  opacity: number
  map?: IPBRTexture
}

export interface ILUXDigitalMaterial {
  transparency: number
  type: string
  preset: string
  ka: string
  ka_bf: string
  kd: string
  kr: string
  ks: string
  ks1: string
  ks2: string
  ks3: string
  ks_bf: string
  r1: string
  r2: string
  r3: string
  m1: string
  m2: string
  m3: string
  kt: string
  d: number
  d_bf: number
  weft_kd: string
  weft_ks: string
  warp_kd: string
  warp_ks: string
  repeat_u: number
  repeat_v: number
  interiorior: number
  exteriorior: number
  cauchyc: number
  uroughness: number
  uroughness_bf: number
  vroughness: number
  vroughness_bf: number
  index: string
  index_bf: number
  multibounce: boolean
  multibounce_bf: boolean
  sigma: number
  p1: number
  p2: number
  p3: number
  thickness: number
  fresnel: {
    type: string
    name: string
    kr: string
    file: string
  }
  volume: {
    absorption: string
    emission: string
    type: string
    scattering: string
    asymmetry: string
    multiscattering: boolean
  }
  shadowcatcher: {
    enable: boolean
  }
  emission: {
    default: string
    gain: string
    power: number
    efficiency: number
    gamma: number
    theta: number
    importance: number
  }
  hash: string
}

export interface IDigitalMaterial {
  id: string
  title: string
  pbr: IPBRDigitalMaterial,
  lux: DeepPartial<ILUXDigitalMaterial>,
  thumbnail: string
  lastUsedDate?: Date
}

export enum PlaceholderBoxStates {
  Warning = 'warning',
  Error = 'error',
  Success = 'success'
}

export interface IPlaceholderBox {
  bBox?: [[number, number, number], [number, number, number]],
  info?: any
  description: string
  state: PlaceholderBoxStates,
  progress: number
}

export interface IObjectWelding {
  angle: number
  baseCalcId: string
  calcId: string
}

export interface ITemporaryArrayToolConfig {
  gapSize: [number, number, number]
  arraySize: [number, number, number]
  itemOffset: [number, number, number]
}

export interface ITemporaryMirrorToolConfig {
  currentAxis: string
  distance: number
}

export interface ITemporarySnapToolConfig {
  ids: string[]
  destId: string
}

export interface ITemporarySculptToolConfig {
  hasChange: boolean
}

export interface ITemporaryToolConfig {
  array: ITemporaryArrayToolConfig
  mirror: ITemporaryMirrorToolConfig
  snap: ITemporarySnapToolConfig
  sculpt: ITemporarySculptToolConfig
}

export enum LightMapEditingMode {
  CreateNew,
  EditApplied
}

export enum BackDropEditingMode {
  CreateNew,
  EditApplied,
  EditLibrary
}

export enum DigitalMaterialEditingMode {
  CreateNew,
  EditApplied,
  EditLibrary
}

export enum SculptWizardScreen {
  Sculpt,
  MakeSolid,
  Rebuild,
  Mask
}

export interface IProfile {
  id: number
  username: string
  email: string
  firstName: string
  lastName: string
  biography: string
  location: string
  avatar: string
  appAdmin: boolean
  isVerified: boolean
  isStaff: boolean
  logInCount: number
  createdDate: Date
  lastLoginDate: Date
  publicProjectCount: number
  privateProjectCount: number
  likeCount: number
  point: number
  followers: { username: string }[]
}

export interface IOrder {
  id: number
  username: string
  orderNumber: string
  amount: number
  createdDate: Date
  status: number
  info: any
  cartItemIds: number[]
}

export interface IWomp20WaterFriend {
  email: string
  bonus: number
  exist: boolean
  sent?: boolean
}

export interface IWomp20Water {
  id: number
  email: string
  point: number
  additionalPoint: number
  isVerified: boolean
  actions: any
  info: { [key: string]: string }
  friendInfo: { [key: number]: IWomp20WaterFriend }
  friends: any
  videos: any
}

export interface IConfiguration {
  defaultProjectIds: number[]
  heroProjectId: number
}

export enum GumballPages {
  Default,
  SelectMaterial,
  SelectVoid,
}

export const defaultProjectDynamic: IProjectDynamic = {
  channelRoutes: {},
  serverCommands: [],
  serverCursor: 0,
  routeStartCursor: 0,
  commands: [],
  cursor: 0
};

export const defaultTemporaryArrayToolConfig: ITemporaryArrayToolConfig = {
  gapSize: [0, 0, 0],
  arraySize: [1, 1, 1],
  itemOffset: [0, 0, 0]
};

export const defaultTemporaryMirrorToolConfig: ITemporaryMirrorToolConfig = {
  distance: 0,
  currentAxis: 'R'
};

export const defaultTemporarySnapToolConfig: ITemporarySnapToolConfig = {
  ids: [],
  destId: ''
};

export const defaultTemporarySculptToolConfig: ITemporarySculptToolConfig = {
  hasChange: false
};

export const defaultTemporaryToolConfig: ITemporaryToolConfig = {
  array: defaultTemporaryArrayToolConfig,
  mirror: defaultTemporaryMirrorToolConfig,
  snap: defaultTemporarySnapToolConfig,
  sculpt: defaultTemporarySculptToolConfig
};

export const defaultDigitalMaterialThumbnail = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAYAAADGWyb7AAAXyklEQVR4Xu1da2xcx3WeWZJSJEtx9Ygsu2piVy0awAkcp04RNC1QNrJryw+JlrS0LImU/IhIxz8aNH2hTaw+frQJ4P5IUCRo4CYoAqQNiiQwoPiBVgYqv1JBsuRKDLXcJak4URIEfdgpZFXcO8W9M2fOY+YuV7t7l9x2Bdi7XO7e3Z3vfud85ztnLrXq/+vJFdA9+an7H1r1gevRk6APXB+4Hl2BHv3Yfcb1gevRFejRj91nXB+4Hl2BHv3Yfcb1gevRFejRj91nXB+4Hl2BHv3Yfcb1gevuCpw+ffompdT7jUl2qsQMK2VuNEYpk/5Pmew2u+9u7c9J+thPk8QcV8o8M6DqL6xZf93UzTff/D/d/fTtv1vPMO7o0aMrt2zZslcr9Tml1BoLEADlblNwVAqW/RnAss9LlEkQUAtiopLssQzQL11eMJ/ctm3bm1pre/Bl/G/ZAzd1durxxCR/pJTZnK2m+5/FDViV3eMMs2gBKOldfx9ZmAKXgeaATVRizCWTLDw7fPv2kWWM2/Js65w8efJdq1au+pJR5j5YPGASsixdbAuYBQKBDEKkf45knHEsdOAlKXApM9P/6imo5029/vi2u3c8v9xAXFaMm5mZ2bSwsPA1Y9Swi4OWYMAeeevzGQXNMS03z2EI9aHUmJB5GXgpI+spiG8t1Osfveu+Xf+6XABcFsAZY0rT05W/V8rs9gsDbGoAmiVZmNeoOAkFChEvPkxCqDSWcekxkXU2F9brql5fUPpysuHOcvnflxrAJQduenrmLq3NPxpjVllmuTQGIoPeghhhITILlkw9WuC4YGFihSpNFCcoYJQTLRnbMHymwFkA60/fu+sBH8aXAsQlA84YM1SpVJ5TSv9GAJjFwYkPeUvUZMY2DloAomNkWB6AKHGhk4oUxzif7zLG2bDp/6svLCT1+vt2lA9M/78BrlKpbDFGf49/YQAEUhqX+xASY2rSq0QJItRxjes6LB0EeJnizB7DfGcfs2Ez/c/U6x/f+cD4X3cbvK4z7vz56j1am6eVF7TAGg4YhDt5iwV2JL9F1ONi+c7VcJjXSP2HYTJlZaYys1AJOS8B8JLk1Mjegx/sJnhdBW5mpvYpo8yfUgHiRaPPZT5OOheEF9m88CZ1HAmJ1C2h+Q5A4m6KUJlBXUdKBcc2UJv1el2l4CXJQsrp75/57vy7jxw5knQDwK4BV6lUn9RafwKBQpnvNIlTiEUCZ9WjJabIcVRN+oLcqkyVOSwQLp3jkoLmQqar+dKDXlwYvGZruVy+VDR4XQGuUpl9UmvzCRIdQY+gE0JlP8lV9i7mu+YYRywvBUARC6wp4CzIKWApQ209B06LEyoOvEyw1OtOUZkfrbv+pi3Dw8MLRYJXOHCVavXTJVX6k8ZMAwfEKUgPXKgoQ+BC2c+9Sm46N884W8vxus4V5A5IAMyKlbpTwYlSifnxrv0PX9ezwFWr8/colTxNvwBzb72cd8BR1jEbCwB0Oc3Xc1bYsCI712SmuYyHSu9pspoO3JRIjpPAmSRTmJA7XSye3bXv4Z8vCrzCGFepvLGlVLryPaXJW0BtRujnNGUgRMB/RI8SFj4OogfPFd/USMbaTuY161Vmp03GLsiBaW5z7gnJfVAKpO9l6zlviXm1CarXnQyf3b3/0d8rArxCgEuL69rsfLzHxawsZJps00jgcs1kx7iQddRQtgzDeg+KbugeUAYScKGGg4IcVCUFLisPbI7zqpWUJUnJ3FF+8NGOm9SFADdTmz1WAkcEOjERltmoRgtvDIXNAhcIl5yyAC0vYJg0m3PKAlceWBfFssx6mbKuW2D9Pwi/6dffvf+RUqdZ13HgZubm7iol6qgPkS6pAUAUrEbAWQahaEHGxbxJ2jiVYkR2waNdcdKTowCSRqtjHwCXqUxng0FBDs1Zr4Rd9yIxycnygY/9cifB6yhwqcs/N3fhp0YpaxgzlrlqLWYiQ+lGnX5pMvvfRXpvJFzKkBnrDlDDmf3e5blM/kNfjjZZmf2FDLS5zjIurfkkcOnj2iQf3j12+NVOgddR4Kq1+a9rrbA10yxwAGYMOFrDkaYpY6BoosYaqSGAEPJiIRPGHJxPyXIcjDyA6QwApjnOshk789Byso+drf5g6MiRIx2p7zoGXK1Wu07pgR9Gz6iAZbw+862cHOCY40/kftSHlC2dcFjIqcjmchwW4ACYBQhsL+pd+pPJnWDYKwQw1RPlsY+h5dcG/ToH3OzcsbRFo5XGrjXcaxE4e/KKRillnZjowpJADAtFnBIbDlH+ezAcaxqHSigHiJuS5jzKNpiBARDdz+Wxwx1Z844cpFKpvGtgcMWPU9ACh4Sax1Bj2Swgem50tI783qtE7Ltx47iBGJEDRIx92PW2OY/WeCRUMlWJlpdnnLPCbPuHhEny3m6UCZqMf1EeO/yHbZAte2lHgKvNzn9LK806wuiQAEAUrBhw7nm0OUqLaeqIsPYNL8jDhimt56iiFEABqCkAUJBTJwVMZpOyjHqXrs3jPE0IlzTPCeBUJ1jXKeAMZZvTj+6kygGOASQZRgd++PCP9yEFeNw5gXYPBQ2BgkKchcoUDMc6pioXYxwtvJnytezzYgUTuarXk7G9hyb/rh3WtQ1cbW7ucW1Kn6POP/MjmwqPvANA5TQbcKVzlGKRGNN8eeDCl5wxgY64HBYi7RwxLIsTX26wFrsFxDFx78MYJibRHJgXy+MTNywpcLOz8xeV1pvDmg2znVP0DfKaAI6yMbgvJ5UXL7ipa8KsL5nbxOAQji5giyfW2uH+pPs8gbKEOORYuGr9ynK53PLoe1uMq1QqKwcHV77NMqVXkBa4cJCV5zrmUYrwSVUi61oHHYBmwIsYzGwsnUw0Z3I/1tYBywsaq2iB5eY0oopt89EtkDZPlMcmWi4N2gJudn5+XBn9ZUZ5CRzSDTsArBaD79KgZSMd/0AtovxPnQto9cB+Aev+c5GCKlKayvZ51tqCYSH82YZI9CqpsRwU4JCHSX7zw4dGmdHxwy17mG0BV61deKtUUmsocAQnEhobsEx0uNGfFL22oF2DrgQPhaE3GbO4sG0jC3FeCtD2Tmx8wTNNyn+X2+j38Yxz3/myXr1mbGzsv1vJdW0BNzt7wTQKk1irYQ7zwiMHsFjolIrRK0sqBoIaDQAk1hZjngiNzg3xNR2oTG8uc5b5/JfrkmBYpCUCMi5bul8tj0+83FXg5ubmbjKmVKPAcbYtApZFkG3WQDXpdJmfi8TiO1tYluNSAKALDmBA6IwPBNEmq91mRQpwMuUVEyegJqHg9qBQlcvcEidKgIHefDCqpNUX9oxNTHYVuFrtwn1aq281BxzWcoxRTYRJyzYBJLCL1XKYw3LH74L9cRQwpxxdw9WHRS9gUJDYHJgOB7kGLZyELKdRF8W3PyxsUCIo8x8PjE+u7ypws3MXnlJGHQLgArb5hEykvvuCQbikwCwyT+L9SxIamfggU8ueFf65pAvOduiA/cWFihcokfE8WlbY7+PGKQnzMP9R4DDKpK8bHZ9oKV219KL0DWuz87Na6RuxWuPnDTArntOoWJFqMgyTYZtGhE4S6uJsk8yi3iRt4eRst4KiO7O7nNIkbJcABeHTn7AY5iHXLQVw3ubiTkle7ZbjjniGEcBIY9R2B8QkV45rQtWld/8j7oi3vNjUMoBpWUcVJLXAbIiEky28pXnbdzYocD402VVbGEy27tv3WO1qw2U7jDN5HnUzbMO8BezDoVea02SOC9gHAoaEQ/tQRJiI8Tto5YA4CbYV+1qOdsTRRouVAnIqDf1KOKHhNLe3uqTuKB+YuOphoo4DR52SWDnAdtsIcRKUAnIfnD/TpWqMzUnCeJ0rqEU45SBxVRnUbn63TprHiIEtem20d0hBddLZb3u27LLAJYk5tPfQJDcxmqBfh4GjnYAwNOaLEsK6QEHmKMqAaXnOCAgPWWjTx+VQkPAmiYNCu9qNSwFUj42A00r9Tnl84skmsGJPaR242jwpvtPDCNBiqtLPhjQOj1H5H8tzQdEtuwH0Ehlu4DUNl+kbwABsg905aCgjI8FSo3kuCiZpFOMcCjINGKdM8qnRg4/9+RIBh94phAHIwUG+EyN3LHQytjVgGm3bOCDQi4SCPGIa07zH+myyHCC7c5yiVIqcFNn60463A4SIFhk28cR2jopHynx6dHzyz7oGXLU2x5qnVFnGxUmzJYAELB9A2oML5keYhSVkPjCVTCqjqYwg0rkTAILab4xJztxmU17EXEbbi7JOqZI2v79nbPIzSwIcKwegNeMksIuOZGI5ph5Fl1uWA87SyoJcVEVK9z/m+NNxPCf5iSsCCpMNCbndOs5TsyMN3oZzfbXA6qJpwM8dRoVJetySVhN7xie/2D3gqnMux9FNHX5emeU8VIuNchv+jk53xeQ/fSxlGvUeWaeAbOSwoTQE1I/feQHC6zhvT8l5STEYRPMcijAYXYiXAu6EGBk99Ng3lwA495asDxd6k9jewAIdz2AaDq82VJKclgMOOv5WoGBBLcoAsckDXueBEIW/lPw0HALgXlEKk9lWBEatWP2OW0fKh17rGnAz1bm3tMJeXHSqC4SINJMDt0SEyjxTmXYTmCMCtRrxImNsC0YTZFeAXZSNCBDi3EStLmQ9FNwwxGvVI/cnnWuePb7pPe9dNTw8/Hb3gJuZ/bbS6s5gAJYwT3YCom6JBykCnjvD88Ilb8dE6jRhabHdpVCQM4DBh3SmMbO2wB8lewPE0CtlGQWOuimgum39bdTowcmWSrKWXpS+ZaVS/W1dKv0Vqlr/kdwd2FXjQqNgGa/VGoVKe7ZSBQkhNub+w2UMeTgUmxSd4pTTyuCmMHdkkdwGllxgf3lXJS+/2Vg5enCypfGFloGr1Wq3JIlmsdmHS1CW7YTKptjWwBWBOg0237PdN9wZYcwVqpGJE2kuxxQlPEaBk/kNFkqbZ0fHJ++82jCZPr9l4IwxK2aqs5ftm7rDuKrb26hYhbvIQPa2xboC0dyWxzahEvlFQ8kFZ6x7wtnFC27PtDzxIR+PdLt9OGwaOKOGhobK9+975OtdBc6FSxNuYASVKUKlcEyCUMkWB8MLk/eBxRVhHBEgFizhikRLBKtMmXUlQyQVJTHg/HAQnpyhMKHS26i1a9f+3PZd+99YAuBm/kYp/QgwDhOyc+JkyAzAc/mLzpD4BUKm0XAVXp7X7rqxF5GRKhFzG+sG+Bxnfx86/mSGhXw2+zx33ZRG6pI6Jnlhsg1h0laoTF9crVavrdfVf8YvPIOMA3lCe2tY11HwQBk2Zpxvkgpbi85Bpu8lbSwcRcArw8atLKIqYwBFQ6pVibwQzy8DtFIvlA9O2guqtvCv5RznilJdqVTTucBVKeucmYXGN815nn0CKFeI2pAYAyzS/Sa9NSyuyUVCPaDuMk7wMy0PQGgIEKCIltZW8HMUUH+KZgAG9Rvpfl+7dtMNd+7adbEFzKiqaPXlSk1PV76hlN6ZsY6Ylr6GA2DkLQPJnq2Yz3hNJ/McWFx8qJXPjnjGsU339jn0vWRea0ZFAohhiJUzJZRxJL9pc6k8NnFNO1dbb4txAPf0dAUrARcx/A1lmmcXBUrcJ2UABcye4I3n/32BDaLEX93VDrP6WpCcJI2AkwDZn+2IO75O5DyS3/xzsoVizsRXRg9NHmydLm2UA/RNp6bOn9da/SK7BqWD0g+vkpDowiwuAFF0WGi7ohz6baAoPWNAjBDHRIgTWgLgHGTzjIMRPP954SI3IkyyfE3qtzhwRm1934dW3HbbbVeWHLhz587dXtKDz8EHkXUcnTyGRcDFwDYP5hGhKKNlAJ8pyfazZbksVqPZDRw2xPKwnM+4cCgoL+9x1wRijWWYm1HzaaRU0sf3jB3+9XZAS1/bkVCZHui7U9NvGqXWevAQPWuzkgXz4Lkwil4ktb5ozovdp/01yjq8Jhe3xNCAZicNDX1O9ARKs5HtJdo7kNtBnLjTBOojtXHT5l/46PaR6rIB7uzZs7+i9QC7AIscX8DED2c9cUVI87SxqQy1mst5LDxiHWcvEOpYKRqwgTghi8/eWxbd2bQyZa19D4wi9gRyDzi1hmWR1upEeXziQ+2C1lHGpQc7d/acn7XEzjeC5L+0ZJpbjDzAmIrM66fREoAIGWyyguKDxXW3EIYjIEUVZqyGy74iESk0zxGgN6y7YfO2HTt+tOyAm5qa2pDUzU9YrmNuCZX5kfsil/mQFdmUGOxvI/05GuoaMkgyMQOF7y/Iy4FwQtD8Zp8LecHniixMDg4MfPX+A4/u7wRoHWdcesB/e/3c00qZe3LBY+UBnPVcjDCLywuOeL9NXjkhmEshTGKh2jMNL/0Lk1tBES4ApfUbK7I9G7lAUdpc2tNm3SYB75g4oQd+/czrC0rpAS5UQl8Sc43oAMhaLraHLdgeLC6HQTzPZgtuX6dlaspd74TMtMQKbmQkqlWrRHw9pNZt2vjB27fvPtUpthXCuPSgZ06cea8aUlO03cOVpXRGoLvsWOUvZ0HVJKhIXFCeE51zIkxqudjeL/VCgw4bxWo8vGp62D2Q08pEnDjghoZWPDXy4EMPdxK0woDLwDt15uNGmc/zkEn6ccLy8i5JsN03Z1+Az4c8J8kwy6U9LjQW+ly02H1uXLjk/uxHFzCvwftbZakulA9OvKfToBUKXHrw1069dkoZ9QEMmbFGKukIMHEiQh9RfwHT2IgeWXQGADew410B/lorQOjEMsxSkmN5BRmy7Yafuf6dv7Zjx1s9B1wG3smT3zdGZVfRwSIcvngoSuLuSfyPPDCpT3xMyRBW/MtuQIPiOuqUiNYNGwSCYyul9owdLrVjIi8GdiHihL5petXY106eesMYc70FjxawfOwNTWWR8yKLHV4hiLADhotkbZYbAsGxoe5KjH0w4IrswtCIw6/Xbrzu1jvuHrnqWcnFwKK/Lxy49M1eeumlVe8YXDGntNpk5ToAQy2unI53rLaTj7kNGSxvuecsmuMa5TPS8Q67Aihk0Pk3as3qNdvu2rP/n64GhFae2xXg0g924sSJoVKSvGGU2mQbppRVKKVpK0cWz/R3NEzifb6Dxj7fsYi9H1l0ADiyL8CfCLGmqQ+ZKEw2rN/0gd+89/7TrQBxta/pGnDwwU68+uqsMupGzzxidzUGChgJ/TDem+P5DkcIpI3WUDHmsY88Hi+4jTKr1g+Wy+X077R05V/XgUu/1XdeeeWz2phP2oiJ7Y9YqMseE2xBMPheOA9e1C2J56yoHxmwz82guGYo7hHIjnnhmoE179++f/+bXUHMvcmSAJe+9ysvvnhHSetnjDGa9utoeAIhEwDaYLtVLnhNqclG013ODSGhdXBw6G93PvjQQ90EDN5ryYCDD/CdF4+fTIy5lc41UsByDWMqPphFRhqmUIcBUyDnMYEExjJ3QQIm0npNmUvXbtjwkU7bWFdzAiw5cBn7jh//sEnqLzPF2aDVQ71HmcOizgkTIOTvyWVgyGFYMlPiCnvaAdADA18d2fvQgSJrtGYAXBbApR/02LFjgyuU+mOlzRN8vIA7HosBFf6e/+UNmzPJtijWARcK1P3RQMv65MT69evuHb67HP/bCs2sdgefs2yAo9/p+D8//xmj9O9imOQFOc1ji9VuFEjeJaDMAsCcUCJuSkmrF1esXjO+feSBtscNOohb52ZOOvmh4Fj/8vwzY0arvzRGbab2k3dfSCHOwyf985o4bmCPwb3HmKo0xryttP6Hd//SLY+0O41VxLqkx1yWjJNftnL06MofDJk/SBLzhEpVaM5+OVaUswFbEhrlTCUb9lEvbNy4Yd9Hbr/v4lLnsMUA7wng6Jc4/eyz1/xEXbnFXLkyrpXakyizLnRRIt4jUZ7u+enugudWDg49pQcHX/6tnXvFH5RfbOmW9vc9B1zech39xte2Dg6qrUld/WySmA1KmdXaaK2MuVxP6v9llP7hwMqBuVXv3DzVyp7rpYUpfPf/M8Att4Ut+vP0gSt6hQs6fh+4gha26MP2gSt6hQs6fh+4gha26MP2gSt6hQs6fh+4gha26MP2gSt6hQs6fh+4gha26MP2gSt6hQs6fh+4gha26MP2gSt6hQs6fh+4gha26MP+LygQpRJaa1eUAAAAAElFTkSuQmCC';

export const defaultPBRDigitalMaterial: IPBRDigitalMaterial = {
  color: '#808080',
  sheen: '#000000',
  clearcoat: 0,
  clearcoatRoughness: 0,
  metalness: 0,
  roughness: 1,
  opacity: 1
};

export const defaultLUXDigitalMaterial: DeepPartial<ILUXDigitalMaterial> = {
  type: 'matte',
  kd: '#808080'
};

export const defaultDigitalMaterial: IDigitalMaterial = {
  id: '',
  title: 'default',
  pbr: defaultPBRDigitalMaterial,
  lux: defaultLUXDigitalMaterial,
  thumbnail: defaultDigitalMaterialThumbnail
};

export interface IFetchAsync {
  fetching: boolean
  message?: string
}

export interface IFetchItemAsync<T> {
  fetching: boolean
  message?: string
  id?: T
  extraInfo?: any
}

export interface IFetchItemsAsync<T> {
  fetching: boolean
  message?: string
  items?: T[]
  extraInfo?: any
}

export interface NormalizedObjects<T> {
  byId: { [id: number]: T }
  allIds: number[]
  config?: any
}

export interface NormalizedStringIndexedObjects<T> {
  byId: { [id: string]: T }
  allIds: string[]
}

export interface IPostItemAsync<T> {
  posting: boolean
  message?: string
  id?: T
  extraInfo?: any
}

export interface IPostAsync {
  posting: boolean
  message?: string
}

export interface IPostItemsAsync<T> {
  posting: boolean
  message?: string
  items?: T[]
  extraInfo?: any
}

export interface IDeleteItemAsync<T> {
  deleting: boolean
  message?: string
  id?: T
  extraInfo?: any
}